import React, { useEffect, useState, useRef } from 'react';
import { content } from '../data';
import { motion, useInView, animate } from 'framer-motion';

const Counter = ({ value }) => {
  const [displayValue, setDisplayValue] = useState('');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  useEffect(() => {
    if (!isInView) {
      // Sæt en fornuftig startværdi før den kommer i view
      const numberMatch = value.match(/\d+/);
      if (numberMatch) {
        setDisplayValue('0' + value.replace(numberMatch[0], ''));
      } else {
        setDisplayValue('');
      }
      return;
    }

    if (isInView) {
      // Find tallet i strengen (f.eks. "25" fra "25+")
      const numberMatch = value.match(/\d+/);
      
      if (numberMatch) {
        const targetNumber = parseInt(numberMatch[0]);
        const suffix = value.replace(numberMatch[0], '');
        
        const controls = animate(0, targetNumber, {
          duration: 2,
          ease: "easeOut",
          onUpdate: (latest) => {
            setDisplayValue(Math.floor(latest) + suffix);
          },
        });
        return () => controls.stop();
      } else {
        // Hvis der ikke er et tal (f.eks. "Høj"), så bare vis værdien
        setDisplayValue(value);
      }
    }
  }, [value, isInView]);

  return <span ref={ref}>{displayValue}</span>;
};

const About = () => {
  return (
    <section id="om" className="pt-12 pb-24 md:py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="md:max-w-lg md:mx-auto lg:max-w-none lg:mx-0"
          >
            <div className="relative">
              <img 
                src={content.about.image} 
                alt="Lene Jauert" 
                className="w-full h-auto object-cover shadow-2xl relative z-10"
              />
              <div className="absolute -bottom-6 -right-6 w-full h-full bg-[#034b89] -z-0 hidden md:block" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="inline-block bg-[#034b89] text-white font-semibold uppercase tracking-widest text-xs px-4 py-1.5 rounded-full mb-6">
              {content.about.subtitle}
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 uppercase mb-8 leading-tight">
              {content.about.title}
            </h2>
            
            <div className="space-y-4 mb-8">
              {content.about.description.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-slate-600 text-base leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
            
            <p className="text-2xl md:text-3xl font-bold text-[#034b89] mb-10 leading-tight">
              {content.about.highlight}
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 md:gap-8 pt-4 border-t border-slate-100">
              {content.about.stats.map((stat, index) => (
                <div key={index} className={index === 2 ? "col-span-2 sm:col-span-1" : ""}>
                  <div className="text-2xl md:text-3xl font-semibold text-slate-900 mb-1">
                    <Counter value={stat.value} />
                  </div>
                  <div className="text-xs md:text-sm font-medium text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
